import { useCallback } from 'react';
import { useStore } from 'react-redux';
import { useDispatch, useSelector } from 'core/common/hooks';
import { RootInitialState } from 'core/common/store';
import { evalCondition } from 'core/common/utils/evalCondition';
import { useFeatureFlags } from 'core/feature-flags/hooks';
import { getFeatures, saveFeature } from 'core/feature-flags/store';
import { getConditionAttributes } from 'core/funnel/store/selectors';
import { ConditionalFlowAttribute, FeatureFlowAttribute } from '../entities';

export const useFlowConditions = () => {
  const dispatch = useDispatch();
  const featureFlags = useFeatureFlags();
  const store = useStore<RootInitialState>();
  const storedFeatures = useSelector(getFeatures);

  const getAttributesByCondition = useCallback(
    <TAttributes extends Array<ConditionalFlowAttribute>>(
      attributes: TAttributes,
    ): TAttributes[number] => {
      const funnelConditions = getConditionAttributes(store.getState());

      const attrsByCondition = attributes.find((attrs) => {
        if (attrs.condition) {
          return [funnelConditions].find(evalCondition(attrs.condition));
        }
      });

      return attrsByCondition ?? attributes[attributes.length - 1];
    },
    [store],
  );

  const getAttributesByFeature = useCallback(
    <TAttributes extends Array<FeatureFlowAttribute & ConditionalFlowAttribute>>(
      attributes: TAttributes,
    ): TAttributes[number] => {
      const attributesWithFeature = attributes.filter((attrs) => attrs.feature);
      const attributesWithoutFeature = attributes.filter((attrs) => !attrs.feature);

      if (!attributesWithFeature.length) {
        return getAttributesByCondition(attributes);
      }

      const firstAttributeWithFeature = attributesWithFeature[0].feature;
      if (!firstAttributeWithFeature) {
        return getAttributesByCondition(attributes);
      }

      const [featureName] = Object.keys(firstAttributeWithFeature);

      const findMatchingAttributes = (value: string) => {
        return attributesWithFeature.find((item) =>
          [{ [featureName]: value }].find(evalCondition(item.feature)),
        );
      };

      const featureValue = featureFlags.getFeatureValue<string>(featureName);

      if (!storedFeatures) {
        return attributesWithoutFeature[attributesWithoutFeature.length - 1];
      }

      // Return attributes based on feature from storage
      if (storedFeatures[featureName]) {
        const matchedAttributes = findMatchingAttributes(storedFeatures[featureName]);

        return matchedAttributes ?? attributesWithoutFeature[attributesWithoutFeature.length - 1];
      }

      // Put experiment into the storage if it is not there yet
      if (featureValue && !storedFeatures[featureName]) {
        dispatch(saveFeature({ name: featureName, value: featureValue }));
      }

      const attributesMatchedWithExperimentGroup = findMatchingAttributes(featureValue);

      return (
        attributesMatchedWithExperimentGroup ?? getAttributesByCondition(attributesWithoutFeature)
      );
    },
    [dispatch, featureFlags, getAttributesByCondition, storedFeatures],
  );

  return { getAttributesByCondition, getAttributesByFeature };
};
